// services/ProjectService.js
import { projectCachedAxios, noCacheAxios, invalidateCache } from '../../../shared/config/cachedAxiosConfig';
import { PROJECT_ENDPOINTS } from '../config/projectApiEndpoints';
import { getAuthHeaders } from '../../../shared/services/auth/authHeaders';
import { ProjectHeadersService } from './ProjectHeadersService';
import logger from '../../services/logging/logger';
import { extractErrorMessage } from '../../utils/extractErrorMessage';

export const ProjectService = {
  /**
   * Get all projects with user permission validation
   */
  getAllProjects: async (userData) => {
    // TEMPORARY: set REACT_APP_DISABLE_PROJECTS=true in .env to skip this call during local
    // testing that doesn't need Projects (e.g. local backend not yet returning valid JWTs).
    // Remove REACT_APP_DISABLE_PROJECTS from .env to re-enable.
    if (process.env.REACT_APP_DISABLE_PROJECTS === 'true') {
      return [];
    }

    try {
      const baseHeaders = await getAuthHeaders();
      const headers = await ProjectHeadersService.addProjectHeaders(baseHeaders, userData); // ✅ ADDED AWAIT
      
      // Add user information to headers for permission validation
      if (userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }
     
      // Handle both single groupId and multiple groupIds
      if (userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }
      
      const response = await projectCachedAxios.get(PROJECT_ENDPOINTS.getAll, { headers });
      return response.data;
      
    } catch (error) {
      // Check if it's an Axios error
      if (error.isAxiosError) {
        
        if (error.response) {
          // Handle specific HTTP status codes
          switch (error.response.status) {
            case 401:
              break;
            case 403:
              break;
            case 404:
              break;
            case 500:
              break;
            default:
          }
          
        } else if (error.request) {
          // Check for common network issues
          if (error.code === 'ECONNREFUSED') {
          } else if (error.code === 'ETIMEDOUT') {
          } else if (error.code === 'ENOTFOUND') {
          } else {
          }
          
        } else {
          // Something else happened
        }
        
        // Log request configuration for debugging
        if (error.config) {
        }
        
      } else {
        // Non-Axios error (e.g., auth header generation failed)
      }
      
      // Re-throw the error with additional context
      const enhancedError = new Error(`Failed to fetch projects: ${extractErrorMessage(error)}`);
      enhancedError.originalError = error;
      enhancedError.context = {
        userId: userData.userId,
        groupId: userData.groupId,
        groupIds: userData.groupIds,
        endpoint: PROJECT_ENDPOINTS.getAll
      };
      
      throw enhancedError;
    }
  },

  /**
   * Create a new project
   */
  createProject: async (projectData, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = await ProjectHeadersService.addProjectHeaders(baseHeaders, userData); // ✅ ADDED AWAIT

      // Add user info for permission checks
      if (userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }

      // Handle both single groupId and multiple groupIds
      if (userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }

      const response = await noCacheAxios.post(PROJECT_ENDPOINTS.create, projectData, { headers });

      // Invalidate project cache after creating new project
      invalidateCache(projectCachedAxios);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update an existing project
   */
  updateProject: async (projectId, projectData, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = await ProjectHeadersService.addProjectHeaders(baseHeaders, userData);

      // Add user info for permission checks
      if (userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }

      // Handle both single groupId and multiple groupIds
      if (userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }

      // Ensure Content-Type is set
      headers['Content-Type'] = 'application/json';

      logger.debug('ProjectService.updateProject - About to send request', {
        url: PROJECT_ENDPOINTS.updateProject(projectId),
        projectDataType: typeof projectData
      });

      const response = await noCacheAxios.put(PROJECT_ENDPOINTS.updateProject(projectId), projectData, { headers });

      logger.info('ProjectService.updateProject - Response received', { projectId });

      // Invalidate project cache after updating project
      invalidateCache(projectCachedAxios);

      return response.data;
    } catch (error) {
      logger.error('ProjectService.updateProject - Error', { error: error.message });
      throw error;
    }
  },

  /**
   * Delete an entire project
   */
  deleteProject: async (projectId, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = await ProjectHeadersService.addProjectHeaders(baseHeaders, userData); // ✅ ADDED AWAIT
      
      // Add user info for permission checks
      if (userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }
      
      // Handle both single groupId and multiple groupIds
      if (userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }
      
      const response = await noCacheAxios.delete(PROJECT_ENDPOINTS.deleteProject(projectId), { headers });

      // Invalidate project cache after deleting project
      invalidateCache(projectCachedAxios);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create a new folder
   */
  createFolder: async (projectId, path, folderName, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = await ProjectHeadersService.addProjectHeaders(baseHeaders, userData); // ✅ ADDED AWAIT
      
      // Add user info for permission checks
      if (userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }
      
      // Handle both single groupId and multiple groupIds
      if (userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }
      
      const cleanPath = path ? path.replace(/\/+$/, '') : '';
      const fullPath = cleanPath ? `${cleanPath}/${folderName}` : folderName;
      
      const response = await noCacheAxios.post(PROJECT_ENDPOINTS.createFolder(projectId, fullPath), {}, { headers });

      // Invalidate project cache after creating folder
      invalidateCache(projectCachedAxios);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete a folder
   */
  deleteFolder: async (projectId, path, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = await ProjectHeadersService.addProjectHeaders(baseHeaders, userData); // ✅ ADDED AWAIT

      // Add user info for permission checks
      if (userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }

      // Handle both single groupId and multiple groupIds
      if (userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }

      const response = await noCacheAxios.delete(PROJECT_ENDPOINTS.deleteFolder(projectId, path), { headers });

      // Invalidate project cache after deleting folder
      invalidateCache(projectCachedAxios);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update a file
   */
  updateFile: async (projectId, fileData, userData) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = await ProjectHeadersService.addProjectHeaders(baseHeaders, userData); // ✅ ADDED AWAIT

      // Add user info for permission checks
      if (userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }

      // Handle both single groupId and multiple groupIds
      if (userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }

      const response = await noCacheAxios.put(PROJECT_ENDPOINTS.updateFile(projectId), fileData, { headers });

      // Invalidate project cache after updating file
      invalidateCache(projectCachedAxios);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Rename a file or folder
   */
  renameItem: async (projectId, folderPath, oldName, newName, isDirectory, userData, fileContent = null) => {
    try {
      const baseHeaders = await getAuthHeaders();
      const headers = await ProjectHeadersService.addProjectHeaders(baseHeaders, userData); // ✅ ADDED AWAIT

      // Add user info for permission checks
      if (userData.userId) {
        headers['X-User-ID'] = userData.userId;
      }

      // Handle both single groupId and multiple groupIds
      if (userData.groupId) {
        headers['X-User-Groups'] = userData.groupId;
        headers['X-Group'] = userData.groupId;
      } else if (userData.groupIds && userData.groupIds.length > 0) {
        headers['X-User-Groups'] = userData.groupIds.join(',');
        headers['X-Group'] = userData.groupIds[0];
      }

      const cleanPath = folderPath ? folderPath.replace(/\/+$/, '') : '';
      const oldNameForPath = isDirectory ? `${oldName}/` : oldName;

      const payload = {
        folderPath: cleanPath ? `${cleanPath}/` : '',
        name: newName,
        path: `projects/${projectId}/${cleanPath ? `${cleanPath}/` : ''}${oldNameForPath}`,
        directory: isDirectory
      };

      if (!isDirectory && fileContent) {
        payload.content = fileContent;
      }

      const response = await noCacheAxios.put(PROJECT_ENDPOINTS.updateFile(projectId), payload, { headers });

      // Invalidate project cache after renaming item
      invalidateCache(projectCachedAxios);

      return response.data;
    } catch (error) {
      throw error;
    }
  }
};